import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from "react";
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, TextField, InputAdornment, Avatar, Chip,
  CircularProgress, Tooltip, Divider, Fade, Collapse, Alert,
  Select, MenuItem, FormControl,
} from "@mui/material";
import { useTheme, alpha, keyframes, styled } from "@mui/material/styles";
import {
  Close         as CloseIcon,
  Search        as SearchIcon,
  PersonAdd     as PersonAddIcon,
  Share         as ShareIcon,
  Edit          as EditIcon,
  Delete        as DeleteIcon,
  Check         as CheckIcon,
  Visibility    as ViewIcon,
  Lock          as EditAccessIcon,
  Group         as GroupIcon,
  SwapHoriz     as SwapIcon,
  ContentCopy   as CopyIcon,
} from "@mui/icons-material";
import { toast } from "sonner";
import apiClient from "../../API";

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────

const GlassInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    fontSize: "0.875rem",
    backgroundColor: alpha(theme.palette.background.default, 0.7),
    backdropFilter: "blur(8px)",
    transition: "all 0.2s ease",
    "&:hover fieldset": { borderColor: alpha(theme.palette.primary.main, 0.4) },
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.paper,
      "& fieldset": { borderColor: theme.palette.primary.main, borderWidth: 1.5 },
    },
  },
}));

const PermissionSelect = styled(Select)(({ theme }) => ({
  borderRadius: 8,
  fontSize: "0.75rem",
  fontWeight: 700,
  "& .MuiSelect-select": {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.75rem",
    fontWeight: 700,
  },
  "& fieldset": { borderColor: alpha(theme.palette.divider, 0.3) },
  "&:hover fieldset": { borderColor: alpha(theme.palette.primary.main, 0.4) },
  "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
}));

// ─── Permission config ────────────────────────────────────────────────────────

const PERMISSIONS = {
  view: {
    label: "Can view",
    icon: ViewIcon,
    color: "#3b82f6",
    gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    bg: "rgba(59,130,246,0.10)",
    description: "Read-only access to tracker data",
  },
  edit: {
    label: "Can edit",
    icon: EditAccessIcon,
    color: "#a855f7",
    gradient: "linear-gradient(135deg, #a855f7, #7c3aed)",
    bg: "rgba(168,85,247,0.10)",
    description: "Can modify tracker settings and links",
  },
};

// ─── PermissionChip ───────────────────────────────────────────────────────────

function PermissionChip({ permission, size = "small" }) {
  const cfg = PERMISSIONS[permission] ?? PERMISSIONS.view;
  const Icon = cfg.icon;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.45,
      px: 0.9, py: 0.3, borderRadius: 8,
      backgroundColor: cfg.bg,
      border: `1px solid ${alpha(cfg.color, 0.28)}`,
    }}>
      <Icon sx={{ fontSize: size === "small" ? 9 : 11, color: cfg.color }} />
      <Typography sx={{ fontSize: size === "small" ? "0.6rem" : "0.68rem", fontWeight: 800, color: cfg.color, letterSpacing: "0.03em" }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

// ─── UserResultRow ────────────────────────────────────────────────────────────

function UserResultRow({ user, permission, onPermissionChange, onAdd, adding, alreadyShared }) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const name = user.full_name ?? user.username ?? user.email ?? "Unknown";

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.5, py: 1.1, borderRadius: 2.5,
        border: `1px solid ${hovered ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.divider, 0.1)}`,
        backgroundColor: alreadyShared
          ? alpha(theme.palette.success.main, 0.04)
          : hovered ? alpha(theme.palette.primary.main, 0.03) : "transparent",
        transition: "all 0.15s",
        animation: `${fadeUp} 0.18s ease`,
      }}
    >
      <Avatar
        src={user.profilephoto ?? user.phphref}
        sx={{
          width: 34, height: 34, fontSize: "0.8rem", fontWeight: 800, flexShrink: 0,
          border: `2px solid ${alreadyShared ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.divider, 0.3)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)})`,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {name}
          </Typography>
          {alreadyShared && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, px: 0.7, py: 0.15, borderRadius: 4, backgroundColor: alpha(theme.palette.success.main, 0.12), border: `1px solid ${alpha(theme.palette.success.main, 0.25)}` }}>
              <CheckIcon sx={{ fontSize: 9, color: "success.main" }} />
              <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, color: "success.main" }}>Shared</Typography>
            </Box>
          )}
        </Box>
        {user.email && (
          <Typography sx={{ fontSize: "0.67rem", color: "text.disabled", lineHeight: 1.3 }} noWrap>
            {user.email}
          </Typography>
        )}
      </Box>

      {!alreadyShared && (
        <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
          <PermissionSelect
            value={permission}
            onChange={(e) => onPermissionChange(e.target.value)}
            sx={{ fontSize: "0.72rem" }}
          >
            {Object.entries(PERMISSIONS).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <MenuItem key={key} value={key} sx={{ gap: 0.75, fontSize: "0.78rem" }}>
                  <Icon sx={{ fontSize: 13, color: cfg.color }} />
                  {cfg.label}
                </MenuItem>
              );
            })}
          </PermissionSelect>
        </FormControl>
      )}

      {!alreadyShared && (
        adding ? (
          <CircularProgress size={20} sx={{ flexShrink: 0, color: "primary.main" }} />
        ) : (
          <Tooltip title="Share tracker" placement="top">
            <IconButton
              size="small"
              onClick={onAdd}
              sx={{
                width: 30, height: 30, borderRadius: 1.75, flexShrink: 0,
                background: "linear-gradient(135deg, #3b82f6, #a855f7)",
                color: "#fff", opacity: hovered ? 1 : 0.7,
                transition: "all 0.15s",
                "&:hover": { transform: "scale(1.1)", opacity: 1 },
              }}
            >
              <PersonAddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )
      )}
    </Box>
  );
}

// ─── SharedUserRow ────────────────────────────────────────────────────────────

function SharedUserRow({ share, onUpdatePermission, onRevoke, isOwner }) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const [updatingPerm, setUpdatingPerm] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const name = share.username ?? share.email ?? `User ${share.shared_with_user_id}`;

  const handlePermChange = async (newPerm) => {
    setUpdatingPerm(true);
    try { await onUpdatePermission(share.shared_with_user_id, newPerm); }
    finally { setUpdatingPerm(false); }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try { await onRevoke(share.shared_with_user_id); }
    finally { setRevoking(false); }
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.5, py: 1, borderRadius: 2.5,
        border: `1px solid ${hovered ? alpha(theme.palette.divider, 0.25) : alpha(theme.palette.divider, 0.08)}`,
        backgroundColor: hovered ? alpha(theme.palette.action.hover, 0.4) : "transparent",
        transition: "all 0.15s",
      }}
    >
      <Avatar
        sx={{
          width: 32, height: 32, fontSize: "0.75rem", fontWeight: 800, flexShrink: 0,
          background: "linear-gradient(135deg, rgba(59,130,246,0.3), rgba(168,85,247,0.3))",
          border: `1.5px solid ${alpha(theme.palette.divider, 0.3)}`,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1.2 }} noWrap>{name}</Typography>
        {share.email && share.username && (
          <Typography sx={{ fontSize: "0.65rem", color: "text.disabled", lineHeight: 1.3 }} noWrap>{share.email}</Typography>
        )}
      </Box>

      {isOwner && (
        <>
          {updatingPerm ? (
            <CircularProgress size={16} sx={{ flexShrink: 0 }} />
          ) : (
            <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
              <PermissionSelect value={share.permission ?? "view"} onChange={(e) => handlePermChange(e.target.value)}>
                {Object.entries(PERMISSIONS).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <MenuItem key={key} value={key} sx={{ gap: 0.75, fontSize: "0.78rem" }}>
                      <Icon sx={{ fontSize: 13, color: cfg.color }} />
                      {cfg.label}
                    </MenuItem>
                  );
                })}
              </PermissionSelect>
            </FormControl>
          )}

          <Tooltip title="Revoke access" placement="top">
            <IconButton
              size="small"
              onClick={handleRevoke}
              disabled={revoking}
              sx={{
                width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
                opacity: hovered ? 1 : 0,
                color: "text.disabled",
                transition: "all 0.15s",
                "&:hover": { color: "error.main", backgroundColor: alpha("#ef4444", 0.1) },
              }}
            >
              {revoking ? <CircularProgress size={13} /> : <DeleteIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Tooltip>
        </>
      )}

      {!isOwner && <PermissionChip permission={share.permission ?? "view"} />}
    </Box>
  );
}

// ─── Main Dialog ──────────────────────────────────────────────────────────────

export function TrackerShareDialog({ open, onClose, tracker, isOwner = true }) {
  const theme = useTheme();

  // Search state
  const [query, setQuery]               = useState("");
  const [results, setResults]           = useState([]);
  const [searching, setSearching]       = useState(false);
  const [pendingPerms, setPendingPerms] = useState({});   // userId → "view"|"edit"
  const [adding, setAdding]             = useState(null); // userId being added

  // Existing shares
  const [shares, setShares]           = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);

  // Misc
  const [addError, setAddError]   = useState("");
  const [notFound, setNotFound]   = useState([]);
  const debounce                  = useRef(null);
  const inputRef                  = useRef(null);

  // ── Load existing shares ────────────────────────────────────────────────────

  const fetchShares = useCallback(async () => {
    if (!tracker?.id) return;
    setSharesLoading(true);
    try {
      const data = await apiClient.trackers.getShares(tracker.id);
      setShares(Array.isArray(data) ? data : []);
    } catch {
      setShares([]);
    } finally {
      setSharesLoading(false);
    }
  }, [tracker?.id]);

  useEffect(() => {
    if (open) { fetchShares(); setQuery(""); setResults([]); setAddError(""); setNotFound([]); }
  }, [open, fetchShares]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  // ── Search ──────────────────────────────────────────────────────────────────

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await apiClient.globalUsers.search({ q, limit: 20 });
      const raw = Array.isArray(res?.results) ? res.results
        : Array.isArray(res?.users) ? res.users
        : Array.isArray(res) ? res : [];
      setResults(raw.map(u => ({
        ...u,
        full_name: u.full_name ?? u.display_name
          ?? ([u.first_name, u.last_name].filter(Boolean).join(" ") || null)
          ?? u.username ?? u.email ?? "Unknown",
        email:        u.email ?? null,
        profilephoto: u.profilephoto ?? u.profile_photo ?? u.phphref ?? null,
        user_id:      u.user_id ?? u.id,
      })));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleQuery = (e) => {
    const val = e.target.value;
    setQuery(val);
    setAddError("");
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doSearch(val), 280);
  };

  // ── Already shared set ──────────────────────────────────────────────────────

  const sharedUserIds = useMemo(() => new Set(shares.map(s => s.shared_with_user_id)), [shares]);

  // ── Add share ───────────────────────────────────────────────────────────────

  const handleAdd = useCallback(async (user) => {
    const uid = user.user_id ?? user.id;
    const perm = pendingPerms[uid] ?? "view";
    setAdding(uid);
    setAddError("");
    setNotFound([]);
    try {
      const res = await apiClient.trackers.share(tracker.id, {
        users: [{ identifier: user.email ?? user.username, permission: perm }],
      });
      if (res.not_found?.length) {
        setNotFound(res.not_found);
      } else {
        toast.success(`Shared with ${user.full_name ?? user.username}`);
        await fetchShares();
        // Clear result so the row updates to "Shared" state
        setResults(prev => prev.map(r =>
          (r.user_id ?? r.id) === uid ? { ...r, _justAdded: true } : r
        ));
      }
    } catch (err) {
      setAddError(err?.response?.data?.error ?? err.message ?? "Failed to share");
    } finally {
      setAdding(null);
    }
  }, [tracker?.id, pendingPerms, fetchShares]);

  // ── Update permission ───────────────────────────────────────────────────────

  const handleUpdatePermission = useCallback(async (shareUserId, newPerm) => {
    try {
      await apiClient.trackers.updateShare(tracker.id, shareUserId, { permission: newPerm });
      setShares(prev => prev.map(s =>
        s.shared_with_user_id === shareUserId ? { ...s, permission: newPerm } : s
      ));
      toast.success("Permission updated");
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Failed to update permission");
    }
  }, [tracker?.id]);

  // ── Revoke ──────────────────────────────────────────────────────────────────

  const handleRevoke = useCallback(async (shareUserId) => {
    try {
      await apiClient.trackers.removeShare(tracker.id, shareUserId);
      setShares(prev => prev.filter(s => s.shared_with_user_id !== shareUserId));
      toast.success("Access revoked");
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Failed to revoke access");
    }
  }, [tracker?.id]);

  // ── Copy public link ────────────────────────────────────────────────────────

  const handleCopyLink = () => {
    const url = `${window.location.origin}/tracker/${tracker?.id}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Public link copied!"));
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
     <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: theme.palette.background.paper,
          boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.22)}`,
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 3, py: 2.25,
        background: `linear-gradient(135deg, ${alpha("#3b82f6", 0.07)} 0%, ${alpha("#a855f7", 0.05)} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        display: "flex", alignItems: "flex-start", gap: 2,
      }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
          background: "linear-gradient(135deg, #3b82f6, #a855f7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 6px 18px rgba(59,130,246,0.4)",
        }}>
          <ShareIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.1, letterSpacing: "-0.3px", fontSize: "1.05rem" }}>
            Share Tracker
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
            Invite teammates to view or edit "{tracker?.name}"
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "text.disabled", borderRadius: 1.5, "&:hover": { color: "error.main", backgroundColor: alpha("#ef4444", 0.08) } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* ── Permission legend ─────────────────────────────────────────────── */}
        <Box sx={{ px: 3, pt: 2, pb: 1.5, display: "flex", gap: 1.5 }}>
          {Object.entries(PERMISSIONS).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <Box key={key} sx={{
                flex: 1, display: "flex", alignItems: "center", gap: 0.75,
                px: 1.25, py: 0.75, borderRadius: 2,
                backgroundColor: cfg.bg,
                border: `1px solid ${alpha(cfg.color, 0.22)}`,
              }}>
                <Box sx={{
                  width: 24, height: 24, borderRadius: 1, flexShrink: 0,
                  background: cfg.gradient, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon sx={{ fontSize: 12, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: cfg.color, lineHeight: 1.1 }}>
                    {cfg.label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.6rem", color: "text.disabled", lineHeight: 1.3 }}>
                    {cfg.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── Search ───────────────────────────────────────────────────────── */}
        {isOwner && (
          <Box sx={{ px: 3, pb: 1.5 }}>
            <GlassInput
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Search by name, email or username…"
              value={query}
              onChange={handleQuery}
              onKeyDown={(e) => e.key === "Escape" && setQuery("")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searching
                      ? <CircularProgress size={14} sx={{ color: "primary.main" }} />
                      : <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                    }
                  </InputAdornment>
                ),
              }}
            />

            {/* Error / not-found feedback */}
            <Collapse in={!!addError || notFound.length > 0}>
              <Alert severity="warning" sx={{ mt: 1, borderRadius: 2, py: 0.5, fontSize: "0.78rem" }}>
                {addError || `Not found on platform: ${notFound.join(", ")}`}
              </Alert>
            </Collapse>

            {/* Search results */}
            {results.length > 0 && (
              <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 240, overflowY: "auto",
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(theme.palette.divider, 0.4), borderRadius: 8 },
              }}>
                {results.map((user) => {
                  const uid = user.user_id ?? user.id;
                  const alreadyShared = sharedUserIds.has(uid) || !!user._justAdded;
                  return (
                    <UserResultRow
                      key={uid}
                      user={user}
                      permission={pendingPerms[uid] ?? "view"}
                      onPermissionChange={(perm) => setPendingPerms(prev => ({ ...prev, [uid]: perm }))}
                      onAdd={() => handleAdd(user)}
                      adding={adding === uid}
                      alreadyShared={alreadyShared}
                    />
                  );
                })}
              </Box>
            )}

            {query.trim() && !searching && results.length === 0 && (
              <Box sx={{ textAlign: "center", py: 2.5 }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.75rem" }}>
                  No users found for "{query}"
                </Typography>
              </Box>
            )}

            {!query.trim() && (
              <Box sx={{ textAlign: "center", py: 2, opacity: 0.55 }}>
                <SearchIcon sx={{ fontSize: 22, color: "text.disabled", mb: 0.5, display: "block", mx: "auto" }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }}>
                  Search to find platform users to share with
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ mx: 3, opacity: 0.5 }} />

        {/* ── Current shares ────────────────────────────────────────────────── */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: 1.5, flexShrink: 0,
              background: "linear-gradient(135deg, #3b82f6, #a855f7)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <GroupIcon sx={{ fontSize: 12, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, flex: 1 }}>
              People with access
              {shares.length > 0 && (
                <Typography component="span" sx={{
                  ml: 0.75, fontSize: "0.65rem", fontWeight: 700,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main", px: 0.75, py: 0.1, borderRadius: 4,
                }}>
                  {shares.length}
                </Typography>
              )}
            </Typography>
            {sharesLoading && <CircularProgress size={13} sx={{ color: "text.disabled" }} />}
          </Box>

          {/* Owner row (always first) */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.25,
            px: 1.5, py: 1, borderRadius: 2.5, mb: 0.5,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}>
            <Avatar sx={{
              width: 32, height: 32, fontSize: "0.75rem", fontWeight: 800, flexShrink: 0,
              background: "linear-gradient(135deg, #3b82f6, #a855f7)",
            }}>
              Y
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>You</Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "text.disabled" }}>Owner</Typography>
            </Box>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.4,
              px: 0.9, py: 0.3, borderRadius: 8,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            }}>
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color: "primary.main" }}>Owner</Typography>
            </Box>
          </Box>

          {/* Shared users */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 220, overflowY: "auto",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(theme.palette.divider, 0.4), borderRadius: 8 },
          }}>
            {!sharesLoading && shares.length === 0 && (
              <Box sx={{ textAlign: "center", py: 3.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: "50%", mx: "auto", mb: 1,
                  backgroundColor: alpha(theme.palette.divider, 0.15),
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <GroupIcon sx={{ fontSize: 18, color: "text.disabled", opacity: 0.5 }} />
                </Box>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.72rem", display: "block" }}>
                  No one else has access yet
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem", display: "block", mt: 0.25 }}>
                  Search above to share with teammates
                </Typography>
              </Box>
            )}
            {shares.map((share) => (
              <SharedUserRow
                key={share.id ?? share.shared_with_user_id}
                share={share}
                onUpdatePermission={handleUpdatePermission}
                onRevoke={handleRevoke}
                isOwner={isOwner}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <DialogActions sx={{
        px: 3, py: 2, gap: 1,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        background: alpha(theme.palette.background.default, 0.5),
        justifyContent: "space-between",
      }}>
        <Tooltip title="Copy public page link" placement="top">
          <Button
            size="small"
            startIcon={<CopyIcon sx={{ fontSize: "14px !important" }} />}
            onClick={handleCopyLink}
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: "0.75rem",
              borderRadius: 8, px: 1.5,
              color: "text.secondary",
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.06), borderColor: alpha(theme.palette.primary.main, 0.3) },
            }}
          >
            Copy public link
          </Button>
        </Tooltip>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none", fontWeight: 800, fontSize: "0.8rem",
            borderRadius: 10, px: 2.5,
            background: "linear-gradient(135deg, #3b82f6, #a855f7)",
            boxShadow: "0 4px 14px rgba(59,130,246,0.35)",
            "&:hover": { opacity: 0.9, boxShadow: "0 6px 20px rgba(59,130,246,0.45)" },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}